import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { PlanningDefaultDraftsApiActions } from './planning-default-drafts.actions';
import { PlanningDefaultDraftsHttpService } from '../services/planning-default-drafts-http.service';
import { DefaultDraftItem } from '../../models/default-draft-template.model';
import { planningDefaultDraftsFeature } from './planning-default-drafts.state';
import { CommonsFacadeService } from '../../../commons/angular/state/commons/commons-facade.service';

export const getDefaultDrafts = createEffect(
  (actions$ = inject(Actions),
   planningDefaultDraftsHttpService = inject(PlanningDefaultDraftsHttpService),
   store = inject(Store),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDefaultDraftsApiActions.getDefaultDrafts),
      withLatestFrom(
        store.select(planningDefaultDraftsFeature.selectCursor)
      ),
      mergeMap(([payload, cursor]) => {
        commonsFacadeService.setLoaderOn();
        let paramsCopy = {...payload.params}
        if (cursor) {
          paramsCopy.cursor = cursor;
        }
        return planningDefaultDraftsHttpService.getAll(paramsCopy).pipe(
          map((res: {
            cursor: string,
            list: DefaultDraftItem[]
          }) => PlanningDefaultDraftsApiActions.getDefaultDraftsSuccess({drafts: res.list, cursor: res.cursor})),
          catchError((error: { message: string }) => of(PlanningDefaultDraftsApiActions.getDefaultDraftsFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        );
      })
    )
  },
  {functional: true}
);

export const updateDefaultDraft = createEffect(
  (actions$ = inject(Actions),
   planningDefaultDraftsHttpService = inject(PlanningDefaultDraftsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDefaultDraftsApiActions.updateDefaultDraft),
      mergeMap(({id, payload}) => {
        commonsFacadeService.setLoaderOn();
        return planningDefaultDraftsHttpService.update(id, payload).pipe(
          map((draft) => PlanningDefaultDraftsApiActions.updateDefaultDraftSuccess({draft})),
          catchError((error) => of(PlanningDefaultDraftsApiActions.updateDefaultDraftFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        );
      })
    )
  },
  {functional: true}
);

export const renameDefaultDraft = createEffect(
  (actions$ = inject(Actions),
   planningDefaultDraftsHttpService = inject(PlanningDefaultDraftsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDefaultDraftsApiActions.renameDefaultDraft),
      mergeMap(({id, payload}) => {
        commonsFacadeService.setLoaderOn();
        return planningDefaultDraftsHttpService.rename(id, payload).pipe(
          map((draft) => PlanningDefaultDraftsApiActions.renameDefaultDraftSuccess({draft})),
          catchError((error) => of(PlanningDefaultDraftsApiActions.renameDefaultDraftFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        );
      })
    )
  },
  {functional: true}
);

export const deleteDefaultDraft = createEffect(
  (actions$ = inject(Actions),
   planningDefaultDraftsHttpService = inject(PlanningDefaultDraftsHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDefaultDraftsApiActions.deleteDefaultDraft),
      mergeMap(({id}) => {
        commonsFacadeService.setLoaderOn();
        return planningDefaultDraftsHttpService.delete(id).pipe(
          map(() => PlanningDefaultDraftsApiActions.deleteDefaultDraftSuccess({id})),
          catchError((error) => of(PlanningDefaultDraftsApiActions.deleteDefaultDraftFailure({error}))),
          finalize(() => {
            commonsFacadeService.setLoaderOff();
          })
        );
      })
    )
  },
  {functional: true}
);
